import React, { useState, useRef } from 'react'
import { createCapsule, uploadMultipleImages } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { applyFormat } from '../lib/textFormat'
import './Compose.css'

const MAX_CHARS = 10000
const MAX_IMAGES = 5
const MAX_IMAGE_BYTES = 8 * 1024 * 1024
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
const SUGGESTED_TAGS = ['Philosophy', 'Economics', 'Politics', 'Science', 'Culture', 'Technology', 'History', 'Ethics']

export default function Compose({ onSubmit, prefillContent = '' }) {
  const { user } = useAuth()
  const [content, setContent] = useState(prefillContent)
  const [claim, setClaim] = useState('')
  const [evidence, setEvidence] = useState('')
  const [mindChange, setMindChange] = useState('')
  const [selectedTags, setSelectedTags] = useState([])
  const [capsuleType, setCapsuleType] = useState('thought') // 'thought' or 'debate'
  const [sources, setSources] = useState([]) // array of { title, url }
  const [sourceUrl, setSourceUrl] = useState('') // temp input for new source
  const [showSourceInput, setShowSourceInput] = useState(false) // toggle source input visibility
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [uploadProgress, setUploadProgress] = useState({})
  const textareaRef = useRef(null)

  // Multiple image state - array of { file, preview, uploading, error }
  const [imagePreviews, setImagePreviews] = useState([])
  const fileInputRef = useRef(null)

  const charCount = content.length
  const remaining = MAX_CHARS - charCount
  const tooShort = content.trim().length < 50
  const missingScaffold = !claim.trim() || !evidence.trim() || !mindChange.trim()
  const maxImagesReached = imagePreviews.length >= MAX_IMAGES

  const toggleTag = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    )
  }

  const handleFormat = (marker) => {
    applyFormat(textareaRef.current, marker, content, setContent)
  }

  const handleImageSelect = (e) => {
    const files = e.target.files
    if (!files) return

    const newPreviews = Array.from(files).map(file => {
      // Validate each file
      if (!ACCEPTED_TYPES.includes(file.type)) {
        setError(`${file.name}: Only JPG, PNG, GIF, and WebP are supported.`)
        return null
      }
      if (file.size > MAX_IMAGE_BYTES) {
        setError(`${file.name}: Image must be under 8 MB.`)
        return null
      }
      return {
        file,
        preview: URL.createObjectURL(file),
        uploading: false,
        error: null
      }
    }).filter(Boolean)

    // Check if adding new images would exceed limit
    const totalImages = imagePreviews.length + newPreviews.length
    if (totalImages > MAX_IMAGES) {
      setError(`Maximum ${MAX_IMAGES} images allowed. You can add ${MAX_IMAGES - imagePreviews.length} more.`)
      // Clean up previews for rejected images
      newPreviews.forEach(p => URL.revokeObjectURL(p.preview))
      e.target.value = ''
      return
    }

    setError('')
    setImagePreviews(prev => [...prev, ...newPreviews])
    e.target.value = ''
  }

  const removeImage = (index) => {
    setImagePreviews(prev => {
      const updated = [...prev]
      URL.revokeObjectURL(updated[index].preview)
      updated.splice(index, 1)
      return updated
    })
  }

  const addSource = () => {
    if (!sourceUrl.trim()) {
      setError('Please enter a URL')
      return
    }

    // Validate URL
    try {
      new URL(sourceUrl.trim())
    } catch (e) {
      setError('Invalid URL format')
      return
    }

    // Extract title from URL (domain name as fallback)
    const url = new URL(sourceUrl.trim())
    const title = url.hostname.replace('www.', '')

    setSources(prev => [...prev, { title, url: sourceUrl.trim() }])
    setSourceUrl('')
    setError('')
  }

  const removeSource = (index) => {
    setSources(prev => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async () => {
    if (!user || tooShort) return
    setLoading(true)
    setError('')
    try {
      // Upload all images
      const imageFiles = imagePreviews.map(p => p.file)
      const imageUrls = imageFiles.length > 0 ? await uploadMultipleImages(imageFiles, user.id) : null

      // Create capsule with image URLs, sources, and type
      const structuredContent = [
        `Claim: ${claim.trim()}`,
        `Evidence: ${evidence.trim()}`,
        `What would change my mind: ${mindChange.trim()}`,
        '',
        content.trim()
      ].join('\n\n')
      const sourcesData = sources.length > 0 ? sources : null
      const capsule = await createCapsule(user.id, structuredContent, selectedTags, imageUrls, sourcesData, capsuleType)

      // Clear form
      imagePreviews.forEach(p => URL.revokeObjectURL(p.preview))
      setContent('')
      setClaim('')
      setEvidence('')
      setMindChange('')
      setSelectedTags([])
      setImagePreviews([])
      setSources([])
      setSourceUrl('')
      setUploadProgress({})

      onSubmit?.(capsule)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleClear = () => {
    imagePreviews.forEach(p => URL.revokeObjectURL(p.preview))
    setContent('')
    setClaim('')
    setEvidence('')
    setMindChange('')
    setSelectedTags([])
    setImagePreviews([])
    setSources([])
    setSourceUrl('')
    setShowSourceInput(false)
    setUploadProgress({})
  }

  return (
    <div className="compose">
      <div className="compose-header">
        <div>
          <span className="compose-title">New Capsule</span>
          <span className="compose-note">Incubates 30 minutes · Permanent once live</span>
        </div>
        <p className="compose-microcopy">
          Write less, think more. Seal into incubation, then refine in your guided workspace.
        </p>
      </div>

      {/* Capsule Type Toggle */}
      <div className="compose-type-selector">
        <button
          type="button"
          className={`compose-type-btn ${capsuleType === 'thought' ? 'active' : ''}`}
          onClick={() => setCapsuleType('thought')}
        >
          💭 Thought
        </button>
        <button
          type="button"
          className={`compose-type-btn ${capsuleType === 'debate' ? 'active' : ''}`}
          onClick={() => setCapsuleType('debate')}
        >
          ⚡ Debate
        </button>
      </div>

      <div className="compose-scaffold">
        <div className="compose-scaffold-title">Quality Scaffold (recommended)</div>
        <label className="compose-scaffold-label">
          What claim are you making?
          <textarea
            className="compose-scaffold-input"
            value={claim}
            onChange={(e) => setClaim(e.target.value)}
            placeholder="State one falsifiable claim."
            rows={2}
            maxLength={500}
          />
        </label>
        <label className="compose-scaffold-label">
          What evidence supports it?
          <textarea
            className="compose-scaffold-input"
            value={evidence}
            onChange={(e) => setEvidence(e.target.value)}
            placeholder="Add concrete evidence, data, or references."
            rows={2}
            maxLength={900}
          />
        </label>
        <label className="compose-scaffold-label">
          What would change your mind?
          <textarea
            className="compose-scaffold-input"
            value={mindChange}
            onChange={(e) => setMindChange(e.target.value)}
            placeholder="Define disconfirming evidence up front."
            rows={2}
            maxLength={500}
          />
        </label>
      </div>

      {/* Image gallery */}
      {imagePreviews.length > 0 && (
        <div className="compose-image-gallery">
          <div className="compose-gallery-grid">
            {imagePreviews.map((img, idx) => (
              <div key={idx} className="compose-gallery-item">
                <img src={img.preview} alt={`Preview ${idx + 1}`} className="compose-gallery-thumb" />
                <button
                  type="button"
                  className="compose-gallery-remove-btn"
                  onClick={() => removeImage(idx)}
                  title="Remove image"
                  disabled={loading}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <div className="compose-gallery-count">
            {imagePreviews.length} / {MAX_IMAGES} images
          </div>
        </div>
      )}

      {/* Formatting toolbar */}
      <div className="format-toolbar">
        <button type="button" className="format-btn" onMouseDown={e => { e.preventDefault(); handleFormat('**') }} title="Bold"><strong>B</strong></button>
        <button type="button" className="format-btn" onMouseDown={e => { e.preventDefault(); handleFormat('*') }} title="Italic"><em>I</em></button>
        <button type="button" className="format-btn" onMouseDown={e => { e.preventDefault(); handleFormat('__') }} title="Underline"><u>U</u></button>
        <span className="format-toolbar-hint">select text then click</span>
      </div>

      <textarea
        ref={textareaRef}
        className="compose-textarea"
        value={content}
        onChange={e => setContent(e.target.value)}
        placeholder="What's worth saying slowly…"
        maxLength={10000}
        rows={4}
      />

      <div className="compose-tags">
        {SUGGESTED_TAGS.map(tag => (
          <button
            key={tag}
            className={`compose-tag ${selectedTags.includes(tag) ? 'selected' : ''}`}
            onClick={() => toggleTag(tag)}
            type="button"
          >
            {tag}
          </button>
        ))}
      </div>

      {/* Sources section */}
      <div className="compose-sources">
        <div className="compose-sources-header">
          <button
            type="button"
            className="compose-sources-toggle"
            onClick={() => setShowSourceInput(!showSourceInput)}
          >
            🔗 {sources.length > 0 ? `Sources (${sources.length})` : 'Add Source'}
          </button>
        </div>

        {showSourceInput && (
          <div className="compose-source-input-wrapper">
            <div className="compose-source-input">
              <input
                type="url"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addSource()}
                placeholder="Paste URL (https://...)"
                className="compose-source-url-input"
              />
              <button
                type="button"
                className="compose-source-add-btn"
                onClick={addSource}
                disabled={loading || !sourceUrl.trim()}
              >
                Add
              </button>
            </div>
          </div>
        )}

        {sources.length > 0 && (
          <div className="compose-sources-list">
            {sources.map((source, idx) => (
              <div key={idx} className="compose-source-item">
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="compose-source-link"
                  title={source.url}
                >
                  {source.title}
                </a>
                <button
                  type="button"
                  className="compose-source-remove-btn"
                  onClick={() => removeSource(idx)}
                  disabled={loading}
                  title="Remove source"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && <div className="compose-error">{error}</div>}

      <div className="compose-footer">
        <div className="compose-footer-left">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            multiple
            style={{ display: 'none' }}
            onChange={handleImageSelect}
          />
          <button
            type="button"
            className={`compose-image-btn ${imagePreviews.length > 0 ? 'active' : ''}`}
            onClick={() => !maxImagesReached && fileInputRef.current?.click()}
            title={maxImagesReached ? `Max ${MAX_IMAGES} images reached` : 'Attach images'}
            disabled={loading || maxImagesReached}
          >
            <CameraIcon />
            {imagePreviews.length > 0 ? `${imagePreviews.length} photo${imagePreviews.length !== 1 ? 's' : ''}` : 'Add photos'}
          </button>
          <span className={`compose-count ${remaining < 100 ? 'compose-count--warn' : ''}`}>
            {charCount} / {MAX_CHARS}
          </span>
          {tooShort && content.length > 0 && (
            <span className="compose-hint">Min. 50 characters</span>
          )}
          {missingScaffold && (
            <span className="compose-hint">Recommended: complete scaffold fields for higher-quality posts</span>
          )}
        </div>
        <div className="compose-actions">
          <button
            className="btn btn-ghost"
            onClick={handleClear}
            disabled={loading || (!content && !claim && !evidence && !mindChange && imagePreviews.length === 0 && sources.length === 0)}
          >
            Clear
          </button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={loading || tooShort || !user}>
            {loading ? <span className="spinner" /> : 'Seal Capsule →'}
          </button>
        </div>
      </div>
    </div>
  )
}

const CameraIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
    <circle cx="12" cy="13" r="4"/>
  </svg>
)
